// #region Imports

import { React, s } from 'Imports';
import { StyledEngineProvider, CircularProgress, ThemeProvider } from 'MaterialUIComponents';
import { cx } from '$Utilities/cssHelpers';
import { getLogger } from '$Utilities/logging';
import { ISinglePageApplicationProps } from './spa.types';
import { IApplicationOptions, ApiContext } from '$Utilities/api';
import { IAgreementsServiceInjectedProps, AgreementsService } from '$State/AgreementsFreezerService';
import { IUserManagementServiceInjectedProps, UserManagementService } from '$State/UserManagementFreezerService';
import { IPricingPlanServiceInjectedProps, PricingPlanService } from '$State/PricingPlanFreezerService';
import { IConfigServiceInjectedProps, ConfigService } from '$State/ConfigFreezerService';
import { IIntegrationPartnerDataInjectedProps, IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';

import theme from '$CSS/materialTheme';
import ErrorBoundary from '$Components/errorBoundary/ErrorBoundary';
import { RouterProvider } from 'react-router-dom';
import { createAppRouter } from './router/routes';
import { SnackbarProvider } from 'notistack';
import { useAppInitializer } from './router/components/useAppInitializer';
import { StateViewer } from '$Components/devTools/StateViewer';
import { AppInitializationContext } from '../src/contexts/AppInitializationContext';
import { SiteUnavailable } from './pages/Error/SiteUnavailable';
// #endregion Imports

// Include the global styles
require('$CSS/global.scss');

const styles = require('Application.scss') as {
    active: string;
    link: string;
    logo: string;
    main: string;
    navbar: string;
    page: string;
    pageOuter: string;
    toolbar: string;
    content: string;
    vpLogoToolbar: string;
    contentToolbarPlaceholder: string;
    mainIntegrated: string;
};

const logger = getLogger('Application');

type IApplicationProps = ISinglePageApplicationProps &
    IUserManagementServiceInjectedProps &
    IConfigServiceInjectedProps &
    IIntegrationPartnerDataInjectedProps &
    IAgreementsServiceInjectedProps &
    IPricingPlanServiceInjectedProps;

const _Application: React.FC<IApplicationProps> = (props) => {
    const { isAppInitialized, initialize } = useAppInitializer(props);
    const [router, setRouter] = React.useState<ReturnType<typeof createAppRouter> | null>(null);

    // Track if user has valid session for Geotab Integrated mode
    const isGeotabIntegrated = props.config.isRunningGeotabIntegrated();

    // Access userCredential from state to ensure component subscribes to changes
    const userCredential = props.userManagement.getState().userCredential;

    // Access isSiteUnavailable from state to ensure component subscribes to changes
    const isSiteUnavailable = props.config.getState().isSiteUnavailable;

    // Now use the method which will use the current credential value
    const hasValidSession = props.userManagement.hasValidSession();

    const apiOptions: IApplicationOptions = {
        applicationRoot: props.baseUrl,
        apiBaseUrl: s.rtrim(`${props.apiBaseUrl}`, '/'),
        wrappedFetch: fetch,
    };

    React.useEffect(() => {
        logger.info('Initializing application');
        initialize();
    }, [initialize]);

    // Create router when conditions are met
    React.useEffect(() => {
        // For Geotab Integrated: wait until user has valid session (SessionId is available)
        // For non-Geotab: create router immediately when app is initialized
        if (isAppInitialized) {
            if (isGeotabIntegrated) {
                if (hasValidSession) {
                    logger.info('Geotab Integrated: Valid session found, creating router');
                    setRouter(createAppRouter(props));
                }
            } else {
                setRouter(createAppRouter(props));
            }
        }
    }, [isAppInitialized, isGeotabIntegrated, hasValidSession, userCredential]);

    if (isSiteUnavailable) return <SiteUnavailable />;

    // Show loading if app not initialized OR if Geotab Integrated and waiting for valid session
    if (!isAppInitialized || (isGeotabIntegrated && !hasValidSession) || !router) {
        return <CircularProgress />;
    }

    return (
        <ErrorBoundary>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <SnackbarProvider maxSnack={3}>
                        <ApiContext.Provider value={apiOptions}>
                            <div
                                id="vp-app-root"
                                className={cx([styles.main])}
                                style={{
                                    flexGrow: 1,
                                    height: '100%',
                                    zIndex: 1,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    display: 'flex',
                                    minHeight: 0,
                                }}
                            >
                                <AppInitializationContext.Provider value={{ isAppInitialized }}>
                                    <RouterProvider future={{ v7_startTransition: true }} router={router} />
                                </AppInitializationContext.Provider>
                                <StateViewer />
                            </div>
                        </ApiContext.Provider>
                    </SnackbarProvider>
                </ThemeProvider>
            </StyledEngineProvider>
        </ErrorBoundary>
    );
};

const Application = UserManagementService.inject(
    AgreementsService.inject(ConfigService.inject(IntegrationPartnerDataService.inject(PricingPlanService.inject(_Application)))),
);

export { Application, IApplicationProps };
